/**
 * R14 closure (September visibility) - one tiny contract for the bounded, paged list endpoints.
 *
 * A paged answer is `{ items, total, total_capped, offset, limit, has_more }`. An OLD-style answer (a bare array - older server, a mock, a
 * caller that sent no paging parameters) is accepted too and treated as one page with an unknown total, so a page never breaks on either shape.
 */
export const PAGE_SIZE = 50;

export function unwrapPage(data, offset = 0, limit = PAGE_SIZE) {
  if (Array.isArray(data)) {
    return { items: data, total: null, total_capped: false, offset, limit, has_more: false, legacy: true };
  }
  const items = Array.isArray(data?.items) ? data.items : [];
  return {
    items,
    total: typeof data?.total === "number" ? data.total : null,
    total_capped: !!data?.total_capped,
    offset: typeof data?.offset === "number" ? data.offset : offset,
    limit: typeof data?.limit === "number" ? data.limit : limit,
    has_more: !!data?.has_more,
    legacy: false,
  };
}

/** "Showing 51-100 of 312" (or "of 5,000+" when the exact count is capped; no total -> "Showing 51-100"). */
export function rangeLabel(page) {
  if (!page || page.items.length === 0) return "No rows";
  const from = page.offset + 1;
  const to = page.offset + page.items.length;
  const of = page.total === null ? "" : ` of ${page.total.toLocaleString("en-IN")}${page.total_capped ? "+" : ""}`;
  return `Showing ${from}-${to}${of}`;
}

/** Query-string pieces for a paged request. Empty values are omitted so the default request stays byte-for-byte small. */
export function pagedQuery({ offset = 0, limit = PAGE_SIZE, extra = {} } = {}) {
  const p = new URLSearchParams();
  p.set("with_meta", "true");
  p.set("offset", String(offset));
  p.set("limit", String(limit));
  Object.entries(extra).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "" && v !== false) p.set(k, String(v)); });
  return p.toString();
}

/** The IST calendar-date range sent to the API: `from` inclusive, `to` EXCLUSIVE. The UI's "to" input is the last day INCLUDED, so add a day. */
export function exclusiveTo(inclusiveDate) {
  if (!inclusiveDate) return "";
  const [y, m, d] = inclusiveDate.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return next.toISOString().slice(0, 10);
}
